import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import DatePicker from '../../../../../../../../../../components/forms/date-picker/DatePicker';
import DropDownInput from '../../../../../../../../../../components/forms/dropdown-input/DropDownInput';
import NumberInput from '../../../../../../../../../../components/forms/number-input/NumberInput';
import TextInput from '../../../../../../../../../../components/forms/text-input/TextInput';
import TooltipIcon from '../../../../../../../../../../components/tooltip-icon/TooltipIcon';
import { useGetProductById } from '../../../../../../../../../../hooks/api/products';
import { EMISSION_MAX_FRACTION_DIGIT } from '../../../../../../../../../../shared/constants/products.constant';
import {
  isProductMassApplicable,
  PRODUCT_MASS_SUFFIX,
  resolveProductMassPerDeclaredUnit,
} from '../../../../../../../../../../shared/helpers/product-mass-helpers';
import { startOfTomorrow } from '../../../../../../../../../../shared/helpers/reference-period';
import { textifyDeclaredUnit } from '../../../../../../../../../../shared/helpers/textify-numerical-footprint-values-with-their-units';
import {
  CalculationDeclaredUnit,
  DeclaredUnit,
} from '../../../../../../../../../../shared/interfaces/IComponentDetails';
import UseOfEnergyDropdown from './components/use-of-energy-dropdown/UseOfEnergyDropdown';

const DECLARED_UNIT_OPTIONS = Object.values(CalculationDeclaredUnit).map((value) => {
  const valueText = textifyDeclaredUnit(value as unknown as DeclaredUnit);

  return { name: valueText, value: value };
});

const GeneralInformationSection = () => {
  // Get productId from URL to fetch cached productDetails
  const { productId } = useParams() as { productId: string };
  const { t } = useTranslation();

  const { control, watch, trigger, getValues, setValue } = useFormContext();

  // Fetch product details
  const { data: productDetails } = useGetProductById(productId);

  const unitaryProductAmount = watch('unitary_product_amount');
  const declaredUnit = watch('declared_unit');

  // Trigger validation for some fields once they change
  useEffect(() => {
    if (declaredUnit != null) {
      trigger(['declared_unit']);
    }
  }, [trigger, declaredUnit]);

  useEffect(() => {
    if (unitaryProductAmount != null) {
      trigger(['unitary_product_amount']);
    }
  }, [trigger, unitaryProductAmount]);

  useEffect(() => {
    if (!declaredUnit || isProductMassApplicable(declaredUnit)) {
      return;
    }

    // The mass input is hidden for these units, so derive the value instead of letting the user enter it
    const derivedProductMass =
      resolveProductMassPerDeclaredUnit(declaredUnit, unitaryProductAmount) ?? undefined;

    if (getValues('product_mass_per_declared_unit') !== derivedProductMass) {
      setValue('product_mass_per_declared_unit', derivedProductMass);
    }
  }, [declaredUnit, unitaryProductAmount, getValues, setValue]);

  // Listen for changes in declared unit
  const getUnitForProductMass = useMemo(() => {
    if (isProductMassApplicable(declaredUnit)) {
      return textifyDeclaredUnit(declaredUnit);
    }

    return undefined;
  }, [declaredUnit]);

  const canEnterProductMass = !!getUnitForProductMass;

  return (
    <div className='bg-white px-5 py-4 border-round-2xl flex flex-column gap-4 w-full'>
      <h4 className='font-medium'>
        {t('calculationWizard.sections.generalInformationSection.title')}
      </h4>

      <div className='flex w-full flex-column'>
        <p className='text-md font-medium mb-2'>
          {t('calculationWizard.sections.generalInformationSection.declaredUnit')}
        </p>

        <div className='grid grid-nogutter'>
          {/* Declared Amount */}
          <div className='col-6 pr-2'>
            <NumberInput
              name='unitary_product_amount'
              label={t(
                'calculationWizard.sections.generalInformationSection.inputs.placeholders.declaredAmountPlaceholder',
              )}
              customIcon={
                <TooltipIcon
                  id='unitary_product_amount'
                  tooltipText={t(
                    'calculationWizard.sections.generalInformationSection.declaredUnitTooltipText',
                  )}
                />
              }
              minFractionDigits={0}
              maxFractionDigits={EMISSION_MAX_FRACTION_DIGIT}
              control={control}
            />
          </div>

          {/* Declared Unit */}
          <div className='col-6 pl-2'>
            <DropDownInput
              name='declared_unit'
              label={t(
                'calculationWizard.sections.generalInformationSection.inputs.placeholders.declaredUnitPlaceholder',
              )}
              control={control}
              options={DECLARED_UNIT_OPTIONS}
            />
          </div>

          {/* Product Mass */}
          {canEnterProductMass && (
            <div className='grid grid-nogutter col-12'>
              <div className='col-6 sm:col-4'>
                <NumberInput
                  name='product_mass_per_declared_unit'
                  suffix={` ${PRODUCT_MASS_SUFFIX}`}
                  label={t(
                    'calculationWizard.sections.generalInformationSection.inputs.placeholders.productMassPlaceholder',
                  )}
                  minFractionDigits={0}
                  maxFractionDigits={EMISSION_MAX_FRACTION_DIGIT}
                  control={control}
                />
              </div>
              <span className='col pl-0 text-sm sm:text-base mt-3 pl-2'>
                {t('global.per')} {getUnitForProductMass}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className='grid grid-nogutter'>
        {/* Retrospective or Prospective Pcf Type */}
        <div className='col-6 pr-2'>
          <TextInput
            name='retro_or_prospective_pcf_type'
            label={t('calculationWizard.sections.generalInformationSection.inputs.pcfType')}
            disabled
            control={control}
          />
        </div>

        {/* Partial or Full Pcf */}
        <div className='col-6 pl-2'>
          <TextInput
            name='partial_full_pcf'
            label={t(
              'calculationWizard.sections.generalInformationSection.inputs.partialOrFullPcf',
            )}
            disabled
            control={control}
          />
        </div>

        {/* Reference Period */}
        <div className='col-6 pr-2'>
          <DatePicker
            name='reference_period'
            icon='pi pi-calendar'
            formatDateTime={(selectedDate: Date) => {
              // Custom value rendering: Even though selectionMode is single, we will render the full range & send a Date[] array to API
              const selectedYear = selectedDate.getFullYear();

              return `01.01.${selectedYear} - 31.12.${selectedYear}`;
            }}
            view='year'
            trigger={trigger}
            selectionMode='single'
            hideOnRangeSelection
            maxDate={new Date(new Date().getFullYear() - 1, 11, 31)} // Don't allow future dates
            label={t('calculationWizard.sections.generalInformationSection.inputs.referencePeriod')}
            control={control}
          />
        </div>

        {/* Validity Period End */}
        <div className='col-6 pl-2'>
          <DatePicker
            name='validity_period_end'
            icon='pi pi-calendar'
            dateFormat='dd.mm.yy'
            trigger={trigger}
            selectionMode='single'
            minDate={startOfTomorrow()} // Only future dates are valid
            label={t('calculationWizard.sections.generalInformationSection.inputs.validUntil')}
            control={control}
          />
        </div>

        {/* Use of Energy */}
        <div className='flex w-full flex-column'>
          <p className='text-md font-medium mb-2'>
            {t('calculationWizard.sections.generalInformationSection.useOfEnergy')}
          </p>

          <UseOfEnergyDropdown productCountryCode={productDetails?.country} name='use_of_energy' />
        </div>
      </div>
    </div>
  );
};

export default GeneralInformationSection;
